// BACK STAYS PUT ON A BASE PAGE, AND CLOSES A DETAIL ON A DETAIL PAGE.
//
// THE PROBLEM. The app's working surfaces are base pages a rep lives on all day
// — Chat, the Masonry directory, Activity — and detail views opened FROM them:
// the Activity evidence tables (?detail=…) and a lead's own record. Cmd+[ /
// Alt+← / the mouse's back button is one keystroke away from the keys people
// actually use, and on a base page it threw the rep out of the page they were
// working in and onto whatever they happened to visit before. Losing a filtered
// table you were reading to a mis-hit modifier is the kind of small thing that
// makes an app feel unsafe to work in.
//
// THE RULE the guard implements:
//   • On a DETAIL view, Back does what the reader means by it: closes the
//     detail and returns to the page it was opened from. Nothing here forces
//     that — the detail simply has to be pushed onto history rather than
//     replacing its parent (see Activity's setParam), and then Back is already
//     correct. The guard's job is only to stay out of the way.
//   • On a BASE page, Back stays where it is.
//
// HOW. Not by cancelling anything — there is no reliable way to cancel a
// history traversal after it has happened, and re-pushing a DIFFERENT url after
// a pop leaves the router rendering one page while the address bar shows
// another. Instead the guard keeps a DUPLICATE of the current entry on top of
// the stack while a base page is showing. A Back press then pops from the
// duplicate to its twin: same url, same route, so the router re-renders exactly
// what was already on screen and the rep sees nothing move. The guard then
// re-pushes the duplicate so the next press has something to eat too.
//
// THE COST, stated plainly because it is a real one: on a base page Back will
// not leave the app either. A rep who arrived from another site cannot get back
// to it with Back while a base page is showing (Forward, the address bar, and
// closing the tab all still work, and any detail view backs out normally). That
// is the deliberate trade the rule asks for.
//
// The guard is mounted inside the signed-in Shell only, so the marketing site,
// the landing page and the sign-in flow keep ordinary browser behaviour.

// Query parameters that mean "a detail view is open on top of this page".
// Activity's evidence tables are drawn as a page over their parent, and Back is
// the natural way out of them. ('thread' used to sit here for the Replied
// thread reader; that page is gone and no surviving surface reads ?thread=.)
const DETAIL_PARAMS = ['detail']

// Paths that ARE a detail view rather than a base page. A base page's own
// section root is deliberately absent: /leads/masonry is the directory (base),
// /leads/company/<id> is one company's record (detail).
//
// /chat/<id> is NOT here on purpose. A conversation is where a rep works, not
// something they opened on top of something else — it is a base page whichever
// thread is showing, which is exactly what the rule was asked for.
const DETAIL_PATHS = [
  /^\/leads\/company\//, // one lead's record
  /^\/collaboration\/invite$/, // the invite wizard
  /\/new$/, // every "new X" wizard (e.g. /leads/new)
  /\/edit$/, // every "edit X" wizard
]

/**
 * Is this location a detail view (Back should leave it) rather than a base page
 * (Back should stay)? Pure, so the rule can be unit-tested without a browser.
 */
export function isDetailLocation(pathname = '', search = '') {
  const path = String(pathname || '')
  if (DETAIL_PATHS.some((re) => re.test(path))) return true
  let params
  try {
    params = new URLSearchParams(String(search || ''))
  } catch {
    return false
  }
  // An EMPTY value is not an open detail: ?detail= is how Activity closes one,
  // and treating that as still-open would strand the guard disarmed on a base
  // page — Back would then walk out of the app from the summary.
  return DETAIL_PARAMS.some((key) => Boolean(params.get(key)))
}

// The marker the guard writes onto the entries it creates, so it can tell its
// own duplicate from an entry the router or another page pushed.
const STAY = '__stayGuard'

function stateOf(win) {
  try {
    return win.history.state || null
  } catch {
    return null
  }
}

/**
 * Ensure a duplicate of the current entry sits on top of the stack, so the next
 * Back press pops onto an identical url. Idempotent: an entry the guard already
 * duplicated is left alone, or every render would grow the history stack.
 * `force` pushes even onto a marked twin — the transition out of a detail needs
 * the push for its side effect (pushState truncates forward history).
 */
function ensureDuplicate(win, url, { force = false } = {}) {
  const state = stateOf(win)
  if (!force && state && state[STAY] === url) return
  try {
    win.history.pushState({ ...(state || {}), [STAY]: url }, '', url)
  } catch {
    // A browser that refuses the push (rate limiting after a burst of
    // navigations) just means Back behaves normally for that one press. The
    // guard must never throw into a render to protect a convenience.
  }
}

/**
 * Install the guard. Returns a teardown. Framework-free so it can be driven
 * from a test with a fake history/window, and mounted from one effect in Shell.
 *
 * `read()` returns the CURRENT { pathname, search } — passed in rather than
 * read from `location` here so the caller can hand over the router's location
 * (which is correct during a transition, where the global can lag).
 */
export function installBackGuard(read, win = typeof window === 'undefined' ? null : window) {
  if (!win) return () => {}
  let armed = false
  let url = ''
  let wasDetail = false

  const sync = () => {
    const here = read() || {}
    const path = String(here.pathname || '')
    const search = String(here.search || '')
    url = `${path}${search}`
    if (isDetailLocation(path, search)) {
      // A detail view: leave history alone so Back closes it.
      armed = false
      wasDetail = true
      return
    }
    // FORWARD MUST DIE WITH THE DETAIL. Backing out of a detail leaves the
    // detail's entry AHEAD of us, and Cmd+] would walk straight back into it
    // from the base page — the exact "goes somewhere from a base page" the
    // rule forbids. pushState is the one operation that truncates forward
    // history, so the transition out of a detail forces a fresh duplicate
    // even when a marked twin is already on top.
    const fromDetail = wasDetail
    wasDetail = false
    armed = true
    ensureDuplicate(win, url, { force: fromDetail })
  }

  const onPop = () => {
    if (!armed) return
    // The pop landed on the duplicate's twin — the same url — so nothing moved
    // on screen. Restore the buffer for the next press. Deferred a tick so the
    // router has finished handling this popstate before the stack changes
    // underneath it.
    win.setTimeout(() => {
      if (armed) ensureDuplicate(win, url)
    }, 0)
  }

  win.addEventListener('popstate', onPop)
  sync()
  return { sync, teardown: () => win.removeEventListener('popstate', onPop) }
}
